<template>
  <main-layout
    controlsBg="light"
    :hasControlsBar="false"
    :breadcrumb="getBreadcrumb">
    <div class="container">
      <grid-row column-align="center">
        <grid-col col-size="4">
          <h1 class="header margin-top-xl">
            <chevron
              @click="$router.push({name: 'Settings'})"/>
            {{ $t('health.settings.supervisors') }}
          </h1>
          <alert
            v-if="alertVisible"
            :message="alertMessage"
            :type="alertType">
          </alert>
        </grid-col>
      </grid-row>
      <grid-row column-align="center">
        <grid-col col-size="2">
            <label-input
              :lowercase="true"
              ref="usernameOrEmailInput"
              form-size="s"
              :label="$t('health.supervisor.usernameOrEmail')"
              v-model="usernameOrEmail"
              :type="'text'"
              :error="usernameOrEmailError"
              :errorMessage="$t('commons.mandatoryFieldMessage')">
            </label-input>
        </grid-col>
        <grid-col col-size="2">
          <btn-pill
            class="margin-top-m margin-left-l"
            :btnText="$t('health.supervisor.invite')"
            v-on:click="onSave"
            btn-size="m">
          </btn-pill>
        </grid-col>
      </grid-row>
      <grid-row class="margin-top-xl" column-align="center">
        <grid-col col-size="4">
          <table-component
            ref="supervisorsTable"
            :data="fetchData"
            sort-by="name"
            sort-order="asc"
            :show-caption="false"
            class="datatable"
            filter-placeholder=""
            filterNoResults="">
            <table-column
              show="username"
              :label="$t('user.name')"
              header-class="text-left"
              cell-class="table-component__text-left">
            </table-column>
            <table-column
              label=""
              header-class="text-left"
              cell-class="table-component__text-left">
              <template slot-scope="row">
                <font-awesome-icon
                  v-if="!row.pending"
                  :icon="icon.check"
                />
                <p
                  v-else
                  class="center"
                >{{$t('health.supervisor.pending')}}</p>
              </template>
            </table-column>
            <table-column
              label=""
              :sortable="false"
              :filterable="false"
              header-class="table-component__minimum__width__column"
              cell-class="text-center">
              <template slot-scope="row">
                <font-awesome-icon
                  v-on:click="onDelete(row)"
                  :icon="icon.trash"
                />
              </template>
            </table-column>
          </table-component>
        </grid-col>
      </grid-row>
    </div>
    <ok-cancel-modal
      v-if="showConfirmationDeleteModal"
      v-on:ok="onDeleteConfirm"
      v-on:cancel="onDeleteCancel"
      :title="deleteModalMessage"
      :message="deleteModalMessage"
      :okButton="$t('commons.delete')"
      :cancelButton="$t('commons.cancel')">
      <div slot="body">
        <ul>
          <li>
            {{ supervisorToRemove.username }}
          </li>
        </ul>
      </div>
    </ok-cancel-modal>
  </main-layout>
</template>

<script>

import MainLayout from '@/layouts/MainLayout';
import GridRow from '@/components/shared/grid/GridRow';
import GridCol from '@/components/shared/grid/GridCol';
import LabelInput from '@/components/shared/forms/LabelInput';
import Alert from '@/components/shared/alert/Alert';
import Chevron from '@/components/shared/Chevron';
import TableComponent from '@/components/shared/table/components/TableComponent';
import TableColumn from '@/components/shared/table/components/TableColumn';
import pagination from '@/components/shared/table/pagination';
import FontAwesomeIcon from '@fortawesome/vue-fontawesome';
import {
  faTrashAlt,
  faCheck
} from '@fortawesome/pro-regular-svg-icons';
import supervisorsAPI from 'BRAND/api/supervisors/';
import OkCancelModal from '@/components/shared/dialogs/OkCancelModal';
import BtnPill from '@/components/shared/buttons/BtnPill';

export default {
  name: 'Accessors',
  components: {
    MainLayout,
    GridRow,
    GridCol,
    LabelInput,
    Alert,
    Chevron,
    TableComponent,
    TableColumn,
    FontAwesomeIcon,
    OkCancelModal,
    BtnPill
  },
  data () {
    return {
      supervisors: [],
      supervisorToRemove: undefined,
      icon: {
        trash: faTrashAlt,
        check: faCheck
      },
      usernameOrEmail: '',
      usernameOrEmailError: false,
      showConfirmationDeleteModal: false,
      deleteModalMessage: '',
      /* Table pagination */
      tablePageSize: 10,
      tableState: {
        filter: '',
        page: 1,
        sort: {
          order: '',
          fieldName: ''
        }
      },
      /* UI controls */
      alertVisible: false,
      alertMessage: '',
      alertType: '' // error, warning, success
    };
  },
  created () {
    this.requestData();
  },
  computed: {
    getBreadcrumb () {
      return [
        {
          name: this.$t('menu.settings'),
          link: '/settings'
        },
        {
          name: this.$t('health.settings.supervisors'),
          link: '/user',
          disabled: true
        }
      ];
    },
    getSupervisors () {
      return this.supervisors;
    }
  },
  watch: {
    getSupervisors: {
      handler: function () {
        this.$refs.supervisorsTable.refresh();
      }
    }
  },
  methods: {
    fetchData ({page, filter, sort}) {
      const tablePage = pagination.createPage(
        this.supervisors,
        this.tableState,
        ['username'],
        this.tablePageSize,
        {page, filter, sort});
      this.tableState = tablePage.state;
      return tablePage.result;
    },
    requestData () {
      supervisorsAPI.getSupervisors(this).then(data => {
        this.supervisors = data;
      });
    },
    onDelete (supervisor) {
      this.supervisorToRemove = supervisor;
      this.deleteModalMessage = this.$t('health.supervisor.remove');
      this.showConfirmationDeleteModal = true;
    },
    onDeleteConfirm () {
      const id = this.supervisorToRemove.userId;
      const username = this.supervisorToRemove.username;
      this.supervisorToRemove = {};
      this.showConfirmationDeleteModal = false;
      supervisorsAPI.deleteSupervisor(this, id).then(() => {
        showAlert(this, this.$t('health.supervisor.successDelete', {name: username}), 'success');
        this.supervisors = this.supervisors.filter(value => value.userId !== id);
      }, () => {
        showAlert(this, this.$t('health.supervisor.errorDelete', {name: username}), 'error');
      });
    },
    onDeleteCancel () {
      this.groupToDelete = {};
      this.showConfirmationDeleteModal = false;
    },
    onSave () {
      let isEmail = false;
      if (this.usernameOrEmail.includes('@')) {
        isEmail = true;
      }
      const call = isEmail ? supervisorsAPI.addSupervisor(this, this.usernameOrEmail, null) : supervisorsAPI.addSupervisor(this, null, this.usernameOrEmail);
      call.then(() => {
        showAlert(this, this.$t('health.supervisor.successInvite', {name: this.usernameOrEmail}), 'success');
        this.usernameOrEmail = '';
        this.requestData();
      }).catch((error) => {
        if (error.bodyText.includes('yourself')) {
          showAlert(this, this.$t('health.supervisor.errorYourselfInvite', {name: this.usernameOrEmail}), 'error');
        } else if (error.status === 409) {
          showAlert(this, this.$t('health.supervisor.errorConflictInvite', {name: this.usernameOrEmail}), 'error');
        } else {
          showAlert(this, this.$t('health.supervisor.errorInviteNotFound', {name: this.usernameOrEmail}), 'error');
        }
      });
    }
  }
};

const showAlert = (context, message, type) => {
  context.alertVisible = true;
  context.alertMessage = message;
  context.alertType = type;
  context.showAlert = true;
  setTimeout(() => {
    context.alertVisible = false;
  }, 2000);
};
</script>

<style scoped>

</style>



// WEBPACK FOOTER //
// src/brand/health/layouts/Supervisors.vue